import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import * as FiIcons from "react-icons/fi";
import Icon from "@mdi/react";
import { mdiFinance } from "@mdi/js";

import actionsIcon from "../../../assets/icons/actions.svg";

import {
  Box,
  Flex,
  Text,
  Image,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from "@chakra-ui/react";

import PushNotificationsModal from "./actions/PushNotificationsModal";
import { closeSidebar } from "../../../store/sidebar/sidebarSlice";
import systemBg from "../../../assets/images/DYARKO-LOGO-NEW.png";

import { SidebarOverlay, SidebarWrapper } from "./SidebarStyle";
import { getCurrentUser } from "../../../store/users/usersSlice";

/* -------------------------------------------------------------------------- */
/*  Navigation config                                                         */
/*  - leaf  : { labelKey, to, perm?, end?, icon? , action? }                  */
/*  - group : { labelKey, icon, children: [...] }                             */
/*  `perm` is a permission key (or array = any of). No perm = always visible. */
/* -------------------------------------------------------------------------- */
const NAV_SECTIONS = [
  {
    id: "main",
    items: [
      {
        labelKey: "layout.sidebar.dashboard",
        to: "/",
        end: true,
        icon: <FiIcons.FiHome />,
      },
      {
        labelKey: "layout.sidebar.settings",
        icon: <FiIcons.FiSettings />,
        children: [
          {
            labelKey: "layout.sidebar.info",
            to: "/settings/info",
            perm: "settings",
          },
          {
            labelKey: "layout.sidebar.contracts",
            to: "/settings/contracts",
            perm: "settings",
          },
          {
            labelKey: "layout.sidebar.config",
            to: "/settings/config",
            perm: "settings",
          },
          {
            labelKey: "layout.sidebar.levels",
            to: "/settings/levels",
            perm: "levels",
          },
          {
            labelKey: "layout.sidebar.commission",
            to: "/settings/commission",
            perm: "commission",
          },
          { labelKey: "layout.sidebar.faq", to: "/settings/faq", perm: "faq" },
        ],
      },
      {
        labelKey: "layout.sidebar.documents",
        icon: <FiIcons.FiFilePlus />,
        children: [
          {
            labelKey: "layout.sidebar.terms_conditions",
            to: "/settings/terms_conditions",
            perm: "settings",
          },
          {
            labelKey: "layout.sidebar.privacy_policy",
            to: "/settings/privacy_policy",
            perm: "settings",
          },
          {
            labelKey: "layout.sidebar.refund_policy",
            to: "/settings/refund_policy",
            perm: "settings",
          },
        ],
      },
      {
        labelKey: "layout.sidebar.admin",
        icon: <FiIcons.FiShield />,
        children: [
          {
            labelKey: "layout.sidebar.users",
            icon: <FiIcons.FiUsers />,
            children: [
              {
                labelKey: "layout.sidebar.employees",
                to: "/employees",
                perm: "employees",
              },
              { labelKey: "layout.sidebar.users", to: "/users", perm: "users" },
            ],
          },
          {
            labelKey: "layout.sidebar.role_management",
            icon: <FiIcons.FiKey />,
            children: [
              {
                labelKey: "layout.sidebar.groups",
                to: "/role-management/groups",
                perm: "groups",
              },
              {
                labelKey: "layout.sidebar.roles",
                to: "/role-management/roles",
                perm: "roles",
              },
            ],
          },
        ],
      },
      {
        labelKey: "layout.sidebar.app_actions",
        icon: (
          <img
            src={actionsIcon}
            alt="Actions"
            width="19"
            height="19"
            style={{ display: "block" }}
          />
        ),
        children: [
          {
            labelKey: "layout.sidebar.free_services",
            to: "/free_services",
            perm: "free_services",
          },
          {
            labelKey: "layout.sidebar.push_notifications",
            action: "pushNotifications",
            perm: "notifications",
          },
          {
            labelKey: "layout.sidebar.videos_in_app",
            to: "/videos-in-app",
            perm: "settings",
          },
        ],
      },
      {
        labelKey: "layout.sidebar.gifts",
        icon: <FiIcons.FiGift />,
        children: [
          {
            labelKey: "layout.sidebar.promo_codes",
            to: "/gifts/promo",
            perm: "promo_codes",
          },
          {
            labelKey: "layout.sidebar.points",
            to: "/gifts/points",
            perm: "points",
          },
          {
            labelKey: "layout.sidebar.wallet",
            to: "/gifts/wallet",
            perm: "wallet",
          },
        ],
      },
      {
        labelKey: "layout.sidebar.requests",
        icon: <FiIcons.FiRepeat />,
        children: [
          {
            labelKey: "layout.sidebar.properties_requests",
            to: "/properties_requests",
            perm: "requests",
          },
          {
            labelKey: "layout.sidebar.videos",
            to: "/videos",
            perm: "requests",
          },
          {
            labelKey: "layout.sidebar.disclaimer_requests",
            to: "/disclaimer_requests",
            perm: "requests",
          },
          {
            labelKey: "layout.sidebar.otp_requests",
            to: "/otps",
            perm: "requests",
          },
          {
            labelKey: "layout.sidebar.end_contract",
            to: "/end_contract",
            perm: "end_contracts",
          },
          {
            labelKey: "layout.sidebar.property_verification",
            to: "/property_verification",
            perm: "requests",
          },
          {
            labelKey: "layout.sidebar.partner_verification",
            to: "/partner_verification",
            perm: "requests",
          },
          {
            labelKey: "layout.sidebar.reel_reports",
            to: "/reel-reports",
            perm: "reels",
          },
        ],
      },
      {
        labelKey: "layout.sidebar.finances",
        icon: <Icon path={mdiFinance} size={1} />,
        children: [
          {
            labelKey: "layout.sidebar.rents",
            icon: <FiIcons.FiDollarSign />,
            children: [
              {
                labelKey: "layout.sidebar.invoices",
                to: "/invoices",
                perm: "invoices",
              },
              {
                labelKey: "layout.sidebar.insurance",
                to: "/insurance",
                perm: "insurance",
              },
            ],
          },
          {
            labelKey: "layout.sidebar.installments",
            icon: <FiIcons.FiDollarSign />,
            children: [
              {
                labelKey: "layout.sidebar.invoices",
                to: "/installment_invoices",
                perm: "installments",
              },
              {
                labelKey: "layout.sidebar.diarco_commission",
                to: "/installments_diarco_commission",
                perm: "dyarko_transactions",
              },
              {
                labelKey: "layout.sidebar.bookey_fees",
                to: "/installments_bookey_fees",
                perm: "management",
              },
            ],
          },
          {
            labelKey: "layout.sidebar.refunds",
            to: "/refunds",
            perm: "refunds",
            icon: <FiIcons.FiDollarSign />,
          },
        ],
      },
    ],
  },

  {
    id: "consumer",
    heading: "layout.sidebar.admin_consumer",
    items: [
      {
        labelKey: "layout.sidebar.consumers",
        to: "/consumer/consumers",
        perm: "users",
        icon: <FiIcons.FiUsers />,
      },
      {
        labelKey: "layout.sidebar.tours",
        to: "/consumer/tours",
        perm: "requests",
        icon: <FiIcons.FiServer />,
      },
      {
        labelKey: "layout.sidebar.rents",
        to: "/consumer/rents",
        perm: "rents",
        icon: <FiIcons.FiServer />,
      },
      {
        labelKey: "layout.sidebar.installments",
        to: "/consumer/installments",
        perm: "installments",
        icon: <FiIcons.FiBookmark />,
      },
      {
        labelKey: "layout.sidebar.invoices",
        to: "/consumer/invoices",
        perm: "invoices",
        icon: <FiIcons.FiFileText />,
      },
      {
        labelKey: "layout.sidebar.points",
        to: "/consumer/points",
        perm: "points",
        icon: <FiIcons.FiDollarSign />,
      },
      {
        labelKey: "layout.sidebar.contracts",
        to: "/consumer/contracts",
        perm: "installments",
        icon: <FiIcons.FiFile />,
      },
    ],
  },

  {
    id: "owner",
    heading: "layout.sidebar.admin_owner",
    items: [
      {
        labelKey: "layout.sidebar.owners",
        to: "owner/owners",
        perm: "users",
        icon: <FiIcons.FiUsers />,
      },
      {
        labelKey: "layout.sidebar.properties",
        to: "owner/properties",
        perm: "properties",
        icon: <FiIcons.FiChevronsUp />,
      },
      {
        labelKey: "layout.sidebar.points",
        to: "owner/points",
        perm: "points",
        icon: <FiIcons.FiBookOpen />,
      },
      {
        labelKey: "layout.sidebar.promoted",
        to: "owner/promoted",
        perm: "wallet",
        icon: <FiIcons.FiBookOpen />,
      },
    ],
  },

  {
    id: "erp-income",
    heading: "layout.sidebar.erp",
    subheading: "layout.sidebar.income",
    items: [
      {
        labelKey: "layout.sidebar.wallet",
        to: "wallet",
        perm: "wallet",
        icon: <FiIcons.FiCompass />,
      },
      {
        labelKey: "layout.sidebar.insurance",
        to: "erp/insurance",
        perm: "insurance",
        icon: <FiIcons.FiCompass />,
      },
      {
        labelKey: "layout.sidebar.installment_commission",
        to: "installment_commission",
        perm: "management",
        icon: <FiIcons.FiCompass />,
      },
      {
        labelKey: "layout.sidebar.rent_commission",
        to: "rent_commission",
        perm: "management",
        icon: <FiIcons.FiCompass />,
      },
    ],
  },

  {
    id: "erp-expenses",
    heading: "layout.sidebar.erp",
    subheading: "layout.sidebar.expenses",
    items: [
      {
        labelKey: "layout.sidebar.general_expenses",
        to: "general_expenses",
        perm: "management",
        icon: <FiIcons.FiDollarSign />,
      },
      {
        labelKey: "layout.sidebar.admin_expenses",
        to: "admin_expenses",
        perm: "management",
        icon: <FiIcons.FiDollarSign />,
      },
      {
        labelKey: "layout.sidebar.tech_expenses",
        to: "tech_expenses",
        perm: "management",
        icon: <FiIcons.FiDollarSign />,
      },
      {
        labelKey: "layout.sidebar.salaries",
        icon: <FiIcons.FiDollarSign />,
        children: [
          {
            labelKey: "layout.sidebar.advance",
            to: "/advance",
            perm: "management",
          },
          {
            labelKey: "layout.sidebar.bounce",
            to: "/bounce",
            perm: "management",
          },
          {
            labelKey: "layout.sidebar.deduction",
            to: "/deduction",
            perm: "management",
          },
          {
            labelKey: "layout.sidebar.net_salaries",
            to: "/salaries",
            perm: "management",
          },
        ],
      },
      {
        labelKey: "layout.sidebar.reports",
        icon: <FiIcons.FiRefreshCw />,
        children: [
          {
            labelKey: "layout.sidebar.general_expenses",
            to: "/reports/general_expenses",
            perm: "management",
          },
          {
            labelKey: "layout.sidebar.promoted",
            to: "/reports/promoted",
            perm: "management",
          },
          {
            labelKey: "layout.sidebar.published",
            to: "/reports/published",
            perm: "management",
          },
          {
            labelKey: "layout.sidebar.salaries",
            to: "/reports/salaries",
            perm: "management",
          },
        ],
      },
    ],
  },
];

/* Recursively keep only leaves the user can access, and groups that still
   have at least one visible child (so the full tree to a valid sub-link stays). */
const filterItems = (items, can) =>
  items.reduce((acc, item) => {
    if (item.children) {
      const children = filterItems(item.children, can);
      if (children.length) acc.push({ ...item, children });
    } else if (can(item.perm)) {
      acc.push(item);
    }
    return acc;
  }, []);

const headingProps = {
  textTransform: "capitalize",
  marginBlock: 4,
  className: "text",
  color: "#5e76a1",
  fontWeight: "600",
};

const navLinkClass = ({ isActive }) =>
  `chakra-accordion__button ${isActive ? "active" : ""}`;

const Sidebar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const sidebar = useSelector((state) => state.sidebar);
  // Adjust this path to wherever the logged-in user is stored

  const currentUser = useSelector((state) => state.users.currentUser);
  const permissions = currentUser?.permissions;

  useEffect(() => {
    dispatch(getCurrentUser());
  }, [dispatch]);

  const [showPushNotificationsModal, setShowPushNotificationsModal] =
    useState(false);

  const handleNavigation = () => dispatch(closeSidebar());

  useEffect(() => {
    dispatch(closeSidebar());
  }, [location, dispatch]);

  const visibleSections = useMemo(() => {
    const owned = new Set(permissions ?? []);
    const can = (perm) => {
      if (!perm) return true;
      return Array.isArray(perm)
        ? perm.some((p) => owned.has(p))
        : owned.has(perm);
    };
    return NAV_SECTIONS.map((section) => ({
      ...section,
      items: filterItems(section.items, can),
    })).filter((section) => section.items.length > 0);
  }, [permissions]);

  /* ------------------------------ renderers ------------------------------ */
  const renderListLink = (item) =>
    item.action === "pushNotifications" ? (
      <button
        type="button"
        className="sidebar-linklike"
        onClick={() => setShowPushNotificationsModal(true)}
      >
        <FiIcons.FiChevronRight />
        <span>{t(item.labelKey)}</span>
      </button>
    ) : (
      <NavLink to={item.to} onClick={handleNavigation}>
        <FiIcons.FiChevronRight />
        <span>{t(item.labelKey)}</span>
      </NavLink>
    );

  const renderItem = (item, nested = false) => {
    const key = item.to ?? item.labelKey;

    // Leaf rendered at accordion level (looks like an accordion button)
    if (!item.children) {
      return (
        <AccordionItem border="none" key={key}>
          <NavLink
            to={item.to}
            end={item.end}
            onClick={handleNavigation}
            className={navLinkClass}
          >
            <Flex alignItems="center" height={nested ? "10" : undefined}>
              <span className="icon">{item.icon}</span>
              <span className="text">{t(item.labelKey)}</span>
            </Flex>
          </NavLink>
        </AccordionItem>
      );
    }

    const allLeaves = item.children.every((c) => !c.children && !c.icon);

    return (
      <AccordionItem border="none" key={key}>
        <AccordionButton height={nested ? "10" : undefined}>
          <Flex alignItems="center" justifyContent="space-between" width="100%">
            <Flex alignItems="center">
              <span className="icon">{item.icon}</span>
              <span className="text">{t(item.labelKey)}</span>
            </Flex>
            <span className="collapse-icon">
              <FiIcons.FiChevronRight />
            </span>
          </Flex>
        </AccordionButton>

        {allLeaves ? (
          <AccordionPanel padding={0}>
            <ul>
              {item.children.map((child) => (
                <li key={child.to ?? child.labelKey}>
                  {renderListLink(child)}
                </li>
              ))}
            </ul>
          </AccordionPanel>
        ) : (
          <AccordionPanel paddingStart={3}>
            <Accordion allowToggle border="none">
              {item.children.map((child) => renderItem(child, true))}
            </Accordion>
          </AccordionPanel>
        )}
      </AccordionItem>
    );
  };

  return (
    <>
      {sidebar.isOpen && (
        <SidebarOverlay
          as={Box}
          className="sidebar-overlay"
          position="fixed"
          top="0"
          right="0"
          w="100%"
          h="100vh"
          bg="rgba(0, 0, 0, .5)"
          zIndex="99"
          role="button"
          onClick={() => dispatch(closeSidebar())}
        />
      )}

      <SidebarWrapper className={sidebar.isOpen ? "active" : ""}>
        <Flex alignItems="center" ms={3}>
          <Image w="90px" src={systemBg} />
        </Flex>

        {visibleSections.map((section, i) => {
          const prev = visibleSections[i - 1];
          return (
            <React.Fragment key={section.id}>
              {section.heading && section.heading !== prev?.heading && (
                <Text {...headingProps} fontSize={16}>
                  {t(section.heading)}
                </Text>
              )}
              {section.subheading && (
                <Text {...headingProps} fontSize={12}>
                  {t(section.subheading)}
                </Text>
              )}
              <Box className="sidebar-links">
                <Accordion allowToggle border="none">
                  {section.items.map((item) => renderItem(item))}
                </Accordion>
              </Box>
            </React.Fragment>
          );
        })}
      </SidebarWrapper>

      {showPushNotificationsModal && (
        <PushNotificationsModal
          onClose={() => setShowPushNotificationsModal(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
